import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../../api';
import RoundDetails from './RoundDetails';

// Round history now comes from the backend (GameRoundHistory) via
// /api/games/:game/history?date=YYYY-MM-DD, was Firestore
// dailyGameHistory_{game}/{date}/rounds with cursor pagination. We fetch the day
// once and paginate/search client-side (so Previous works properly now).
const PAGE_SIZE = 10;

const getYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const DailyHistoryViewer = ({ gameType }) => {
    const todayInDhaka = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Dhaka" }));
    const today = getYYYYMMDD(todayInDhaka);

    const [selectedDate, setSelectedDate] = useState(today);
    const [allRounds, setAllRounds] = useState([]); // full set for the day (roundId desc)
    const [selectedRound, setSelectedRound] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchView, setIsSearchView] = useState(false);
    const [searchResults, setSearchResults] = useState([]);

    const fetchRounds = useCallback(async () => {
        if (!selectedDate) return;
        setLoading(true); setError(''); setAllRounds([]); setSelectedRound(null);
        try {
            const rows = await apiGet(`/api/games/${gameType}/history?date=${selectedDate}&limit=500`);
            setAllRounds((rows || []).map(r => ({ ...r, id: String(r.roundId) })));
        } catch (err) { setError('Failed to fetch rounds.'); }
        finally { setLoading(false); }
    }, [selectedDate, gameType]);

    useEffect(() => {
        setCurrentPage(1); setSearchTerm(''); setIsSearchView(false); setSearchResults([]);
        fetchRounds();
    }, [selectedDate, fetchRounds]);

    const handleSearch = (e) => {
        e.preventDefault();
        const term = searchTerm.trim();
        if (!term) return;
        setError(''); setSelectedRound(null); setIsSearchView(true);
        const found = allRounds.filter(r =>
            String(r.roundId) === term ||
            (r.bets && Object.values(r.bets).some(bet => String(bet.customId) === term))
        );
        setSearchResults(found);
        if (found.length === 0) setError(`No round found with Round ID or Custom ID "${term}" for this date.`);
    };

    const clearSearch = () => { setIsSearchView(false); setSearchTerm(''); setSearchResults([]); setSelectedRound(null); setCurrentPage(1); };

    const totalPages = Math.max(1, Math.ceil(allRounds.length / PAGE_SIZE));
    const isLastPage = currentPage >= totalPages;
    const pagedRounds = allRounds.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const displayRounds = isSearchView ? searchResults : pagedRounds;

    const goToNextPage = () => { if (!isLastPage) { setSelectedRound(null); setCurrentPage(p => p + 1); } };
    const goToPrevPage = () => { if (currentPage > 1) { setSelectedRound(null); setCurrentPage(p => p - 1); } };

    return (
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 20 }}>
            <h3 style={{ textAlign: 'center', fontWeight: 700, marginBottom: 16, color: 'rgba(255,255,255,0.7)' }}>Daily Round History</h3>

            {/* Date Selector */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <label style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>Select Date:</label>
                <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#f1f5f9', fontSize: 13 }} />
            </div>

            {/* Search */}
            <form onSubmit={handleSearch} style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by Round ID or Custom ID" className="dark-input" style={{ width: 260 }} />
                <button type="submit" style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#ff4d6d,#ff2d55)', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    <i className="fas fa-search" />
                </button>
                {isSearchView && (
                    <button type="button" onClick={clearSearch} style={{ padding: '8px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 13 }}>
                        Clear
                    </button>
                )}
            </form>

            {loading && <div style={{ textAlign: 'center', padding: '16px', color: 'rgba(255,255,255,0.5)' }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading...</div>}
            {error && !loading && <div style={{ textAlign: 'center', color: '#f87171', padding: '12px', background: 'rgba(239,68,68,0.1)', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)' }}>{error}</div>}
            {!loading && displayRounds.length === 0 && !error && (
                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', padding: '16px' }}>No rounds found.</p>
            )}

            {!loading && displayRounds.length > 0 && (
                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12, marginBottom: 12 }}>
                    <p style={{ textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 10 }}>Click a round to see details</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                        {displayRounds.map(round => (
                            <button key={round.id} onClick={() => setSelectedRound(round)} style={{
                                padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                                background: selectedRound?.id === round.id ? 'linear-gradient(135deg,#ff4d6d,#ff2d55)' : 'rgba(255,255,255,0.07)',
                                color: selectedRound?.id === round.id ? '#fff' : 'rgba(255,255,255,0.7)',
                                border: selectedRound?.id === round.id ? 'none' : '1px solid rgba(255,255,255,0.12)'
                            }}>
                                Round {round.id}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {!isSearchView && (
                <div className="dark-pagination" style={{ marginTop: 12 }}>
                    <button className="btn-page" onClick={goToPrevPage} disabled={currentPage === 1 || loading}>
                        <i className="fas fa-chevron-left" style={{ marginRight: 6 }} />Previous
                    </button>
                    <span>Page {currentPage} / {totalPages}</span>
                    <button className="btn-page" onClick={goToNextPage} disabled={isLastPage || loading}>
                        Next<i className="fas fa-chevron-right" style={{ marginLeft: 6 }} />
                    </button>
                </div>
            )}

            {selectedRound && <RoundDetails roundId={selectedRound.id} round={selectedRound} />}
        </div>
    );
};

export default DailyHistoryViewer;
